
<script>
export default {
  name: 'RcdAvailable',
  props: {
    availableData: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['collections-selected'],
  data() {
    return {
      filterText: ''
    }
  },
  computed: {
    filteredFundGroups() {
      if (!this.filterText.trim()) {
        return this.availableData;
      }
      
      const searchTerm = this.filterText.toLowerCase();
      
      return this.availableData.map(fundGroup => ({
        ...fundGroup,
        officers: fundGroup.officers.map(officerGroup => ({
          ...officerGroup,
          collections: officerGroup.collections.filter(collection =>
            collection.orNumber.toLowerCase().includes(searchTerm) ||
            collection.payor.toLowerCase().includes(searchTerm) ||
            collection.particular.toLowerCase().includes(searchTerm) ||
            collection.collectionItems.some(item =>
              item.fee.toLowerCase().includes(searchTerm) ||
              item.accountName.toLowerCase().includes(searchTerm)
            )
          )
        })).filter(officerGroup => officerGroup.collections.length > 0)
      })).filter(fundGroup => fundGroup.officers.length > 0);
    },
    
    selectedCollections() {
      const selected = [];
      this.availableData.forEach(fundGroup => {
        fundGroup.officers.forEach(officerGroup => {
          officerGroup.collections.forEach(collection => {
            if (collection.selected) {
              selected.push(collection);
            }
          });
        });
      });
      return selected;
    },
    
    selectedTotalAmount() {
      return this.selectedCollections.reduce((total, collection) => {
        return total + this.getCollectionTotal(collection);
      }, 0);
    }
  },
  methods: {
    getCollectionTotal(collection) {
      return collection.collectionItems.reduce((total, item) => total + item.amount, 0);
    },
    
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },
    
    toggleOfficerCollections(officerGroup) {
      officerGroup.collections.forEach(collection => {
        collection.selected = officerGroup.selectAll;
      });
    },
    
    updateOfficerSelection(officerGroup) {
      const selectedCount = officerGroup.collections.filter(c => c.selected).length;
      const totalCount = officerGroup.collections.length;
      
      if (selectedCount === 0) {
        officerGroup.selectAll = false;
      } else if (selectedCount === totalCount) {
        officerGroup.selectAll = true;
      } else {
        officerGroup.selectAll = false;
      }
    },
    
    processSelected() {
      if (this.selectedCollections.length === 0) {
        this.$toast('No collections selected', 'warning');
        return;
      }
      
      this.$emit('collections-selected', this.selectedCollections);
    },
    
    clearSelection() {
      this.availableData.forEach(fundGroup => {
        fundGroup.officers.forEach(officerGroup => {
          officerGroup.selectAll = false;
          officerGroup.collections.forEach(collection => {
            collection.selected = false;
          });
        });
      });
    },
    checkIfHasSelected(officerGroup) {
      return officerGroup.collections.some(c => c.selected);
    }
  }
}
</script>
<template>
  <div>
    <!-- Search Filter -->
    <div class="row mb-3">
      <div class="col-md-6">
        <div class="search-box">
          <div class="position-relative">
            <input
              type="text"
              class="form-control rounded border"
              placeholder="Search collections..."
              v-model="filterText"
            >
            <i class="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Available Collections -->
    <div v-else>
      <!-- Fund Source Groups -->
      <div v-for="fundGroup in filteredFundGroups" :key="fundGroup.fundSourceId || 'null'" class="mb-4">
        <!-- Fund Source Header -->
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0 d-flex align-items-center">
              <i class="bx bx-folder-open me-2 text-warning"></i>
              {{ fundGroup.fundSource || 'No Fund Source' }}
            </h5>
          </div>
          <div class="card-body p-0">
            <!-- Collecting Officer Groups -->
            <div v-for="officerGroup in fundGroup.officers" :key="officerGroup.collectingOfficerId" class="border-bottom">
              <!-- Collecting Officer Header -->
              <div class="p-3 border-bottom">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <label :for="`officer-${officerGroup.collectingOfficerId}`" class="form-check-label mb-0">
                      <h6 class="mb-0 d-flex align-items-center ms-3">
                        <i class="bx bx-user-circle me-2 font-size-16"></i>
                        {{ officerGroup.collectingOfficer }}
                      </h6>
                    </label>
                  </div>
                  <div class="text-muted">
                    Total Amount: <strong>₱{{ formatCurrency(officerGroup.totalAmount) }}</strong>
                    <!-- <button type="button" class="btn btn-sm btn-outline-success ms-3" v-if="checkIfHasSelected(officerGroup)" @click="processSelected(officerGroup)">
                      <i class="bx bx-check-square me-1"></i>
                      Process Selected
                    </button> -->
                  </div>
                </div>
              </div>

              <!-- Collections List -->
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="table-light">
                    <tr>
                      <th></th>
                      <th width="50">
                        <input
                          type="checkbox"
                          class="form-check-input"
                          v-model="officerGroup.selectAll"
                          @change="toggleOfficerCollections(officerGroup)"
                        >
                      </th>
                      <th>OR Number</th>
                      <th>Payor</th>
                      <th>Particular</th>
                      <th>Collection Items</th>
                      <th class="text-end">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="collection in officerGroup.collections" :key="collection.id">
                      <td></td>
                      <td>
                        <input
                          type="checkbox"
                          class="form-check-input"
                          v-model="collection.selected"
                          @change="updateOfficerSelection(officerGroup)"
                        >
                      </td>
                      <td>
                        <span class="fw-bold text-primary">{{ collection.orNumber }}</span>
                      </td>
                      <td>{{ collection.payor }}</td>
                      <td>{{ collection.particular }}</td>
                      <td>
                        <div class="d-flex flex-wrap gap-1">
                          <span
                            v-for="item in collection.collectionItems"
                            :key="item.id"
                            class="text-dark border"
                            :title="`${item.fee} - ₱${formatCurrency(item.amount)}`"
                          >
                            {{ item.fee.length > 20 ? item.fee.substring(0, 20) + '...' : item.fee }}
                          </span>
                        </div>
                      </td>
                      <td class="text-end">
                        <strong class="text-success">₱{{ formatCurrency(getCollectionTotal(collection)) }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Data State -->
      <div v-if="filteredFundGroups.length === 0" class="text-center py-5">
        <i class="bx bx-folder-open display-4 text-muted"></i>
        <h4 class="mt-3 text-muted">No Collections Available</h4>
        <p class="text-muted">No collections found matching your criteria.</p>
      </div>
    </div>

    <!-- Selected Collections Summary -->
    <div v-if="selectedCollections.length > 0" class="position-fixed bottom-0 end-0 m-4">
      <div class="card shadow-lg">
        <div class="card-body">
          <h6 class="card-title mb-2">
            <i class="bx bx-check-circle text-success me-2"></i>
            Selected Collections
          </h6>
          <p class="mb-2">
            <strong>{{ selectedCollections.length }}</strong> collections selected
          </p>
          <p class="mb-3">
            Total Amount: <strong class="text-success">₱{{ formatCurrency(selectedTotalAmount) }}</strong>
          </p>
          <div class="d-flex gap-2">
            <button class="btn btn-primary btn-sm" @click="processSelected">
              <i class="bx bx-plus me-1"></i>
              Add to RCD
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="clearSelection">
              <i class="bx bx-x me-1"></i>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #74788d;
}

.card-header {
  border-bottom: 1px solid #e9ecef;
}

.table th {
  border-top: none;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge {
  font-size: 0.75rem;
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}
</style>